import React, { useState, useRef, useEffect } from 'react';
import { Edit2, Check, X, Camera } from 'lucide-react';
import { useAuth, api } from '../contexts/AuthContext';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { toast } from 'sonner';

// Componente para texto editável inline
export const EditableText = ({ 
  settingKey, 
  defaultValue = '', 
  className = '', 
  as: Component = 'span',
  multiline = false,
  style = {}
}) => {
  const { user } = useAuth();
  const { settings, refetch } = useSiteSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  
  const isAdmin = user?.role === 'admin';
  const currentValue = settings?.[settingKey] || defaultValue;

  useEffect(() => {
    setValue(currentValue);
  }, [currentValue]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = (e) => {
    if (isAdmin) {
      e.preventDefault();
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (value === currentValue) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      const updatedSettings = { ...settings, [settingKey]: value };
      await api.put('/settings', updatedSettings);
      await refetch();
      toast.success('Texto atualizado!');
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setValue(currentValue);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <span className="inline-flex items-center gap-2 bg-white p-1 rounded-lg shadow-lg border-2 border-[#BFA05F]" style={{ zIndex: 1000 }}>
        {multiline ? (
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="bg-transparent border-none focus:outline-none min-w-[200px] max-w-[400px] resize-none text-[#1A1815]"
            rows={3}
            style={{ fontSize: 'inherit', fontFamily: 'inherit' }}
          />
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="bg-transparent border-none focus:outline-none min-w-[100px] text-[#1A1815]"
            style={{ fontSize: 'inherit', fontFamily: 'inherit' }}
          />
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="p-1.5 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
          title="Guardar"
        >
          <Check className="w-3 h-3" />
        </button>
        <button
          onClick={handleCancel}
          className="p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          title="Cancelar"
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }

  return (
    <Component
      className={`${className} ${isAdmin ? 'editable-text cursor-pointer relative group' : ''}`}
      onDoubleClick={handleDoubleClick}
      title={isAdmin ? 'Duplo clique para editar' : undefined}
      style={style}
    >
      {currentValue}
      {isAdmin && (
        <span className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-[#BFA05F] rounded-full p-1 shadow-md">
          <Edit2 className="w-3 h-3 text-white" />
        </span>
      )}
    </Component>
  );
};

// Componente para imagem editável - agora usa URL externa
export const EditableImage = ({ 
  settingKey, 
  defaultSrc = '', 
  alt = '', 
  className = '',
  imgClassName = '',
  overlayPosition = 'center' // center, bottom-right
}) => {
  const { user } = useAuth();
  const { settings, refetch } = useSiteSettings();
  const [isHovered, setIsHovered] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const isAdmin = user?.role === 'admin';
  // Use URL directly - if it's external (http) use as is, otherwise use default
  const currentUrl = settings?.[settingKey];
  const imageSrc = currentUrl && currentUrl.startsWith('http') ? currentUrl : defaultSrc;

  const handleEditUrl = async () => {
    const newUrl = prompt('Cole a URL da nova imagem (Cloudinary, Unsplash, etc.):', currentUrl || '');
    
    if (newUrl === null) return; // Cancelled
    if (newUrl === currentUrl) return; // No change
    
    // Validate URL
    if (newUrl && !newUrl.startsWith('http')) {
      toast.error('URL inválida. Deve começar com http:// ou https://');
      return;
    }

    setSaving(true);
    try {
      const updatedSettings = { ...settings, [settingKey]: newUrl || null };
      await api.put('/settings', updatedSettings);
      await refetch();
      toast.success('Imagem atualizada!');
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Erro ao guardar imagem');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div 
      className={`relative ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <img 
        src={imageSrc} 
        alt={alt} 
        className={imgClassName}
        onError={(e) => { if (defaultSrc) e.target.src = defaultSrc; }}
      />
      
      {/* Admin edit button - always visible when admin */}
      {isAdmin && (
        <div 
          className={`absolute ${
            overlayPosition === 'center' 
              ? 'top-24 right-8' 
              : 'bottom-4 right-4'
          } z-[100] transition-opacity ${isHovered ? 'opacity-100' : 'opacity-70 hover:opacity-100'}`}
        >
          <button
            onClick={handleEditUrl}
            disabled={saving}
            className="bg-[#BFA05F] text-white px-4 py-2 rounded-full flex items-center gap-2 hover:bg-[#9E8246] transition-colors shadow-lg"
          >
            <Camera className="w-4 h-4" />
            {saving ? 'A guardar...' : 'Alterar URL'}
          </button>
        </div>
      )}
    </div>
  );
};

// Adicionar estilos CSS globais
const InlineEditStyles = () => (
  <style>{`
    .editable-text {
      position: relative;
      display: inline-block;
      transition: all 0.2s ease;
    }
    .editable-text:hover {
      background-color: rgba(191, 160, 95, 0.1);
      outline: 2px dashed #BFA05F;
      outline-offset: 4px;
      border-radius: 4px;
    }
  `}</style>
);

export { InlineEditStyles };
export default { EditableText, EditableImage, InlineEditStyles };
